import { useDepartmentText } from 'model/DepartmentTerminology';
import { Flex } from '@mantine/core';
import { ColDef, GridApi } from 'ag-grid-community';
import { HierarchyAvailableLevelsResponse } from 'api/actions/hierarchy-available-levels/hierarchy-available-levels-response';
import { useApi } from 'api/api-context';
import { DepartmentBreadcrumbItem } from 'components/department/DepartmentBreadcrumb';
import DepartmentParentReference from 'components/department/DepartmentParentReference';
import { ObjectTreeColumnFilter, ObjectTreeFloatingFilter } from 'components/tables/hierarchy/ObjectTreeFilter';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import { get } from 'lodash';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import useDepartmentUiMode from 'model/useDepartmentUiMode';

const OBJECT_COLUMN_ID = 'department.departmentId';
const EXPORT_COLUMN_PREFIX = 'departmentHierarchyExport';

type Hierarchy = HierarchyAvailableLevelsResponse[number];

type HierarchyState =
  | { status: 'loading'; hierarchies: Hierarchy[]; error: null }
  | { status: 'ready'; hierarchies: Hierarchy[]; error: null }
  | { status: 'error'; hierarchies: Hierarchy[]; error: unknown };

interface ObjectColumnOptions {
  breadcrumbField: string;
  organizationFilterKey: string;
  permissionSlug?: string;
  legacyColumnIds?: string[];
}

/** Reads the canonical root-to-associated-object path from a table row. */
const rowBreadcrumbs = (data: Record<string, any>, breadcrumbField: string): DepartmentBreadcrumbItem[] =>
  get(data, breadcrumbField) ?? [];

/** Keeps hierarchy-level export columns stable and deterministic across tables and filters. */
export const orderAvailableHierarchies = (hierarchies: Hierarchy[]) =>
  [...hierarchies]
    .sort(
      (left, right) =>
        Number(right.isDefault) - Number(left.isDefault) ||
        left.hierarchyName.localeCompare(right.hierarchyName, 'sk') ||
        left.hierarchyId - right.hierarchyId
    )
    .map((hierarchy) => ({
      ...hierarchy,
      levels: [...hierarchy.levels].sort((left, right) => left.levelOrder - right.levelOrder),
    }));

/** Creates the export heading, qualifying labels used by more than one hierarchy. */
const hierarchyLevelHeader = (hierarchy: Hierarchy, duplicateLabels: Set<string>, label: string) =>
  duplicateLabels.has(label) ? `${label} (${hierarchy.hierarchyName})` : label;

/** Displays the associated object with its canonical parent-path reference. */
function ObjectCell({ data, breadcrumbField }: { data: Record<string, any>; breadcrumbField: string }) {
  const { mode } = useDepartmentUiMode();
  const breadcrumbs = rowBreadcrumbs(data, breadcrumbField);
  const node = breadcrumbs[breadcrumbs.length - 1];
  if (!node) return null;

  return (
    <Flex gap={8} align="center" h="100%" wrap="nowrap">
      {mode === 'tree' && <DepartmentParentReference breadcrumbs={breadcrumbs} />}
      <P2Regular>{node.departmentName}</P2Regular>
    </Flex>
  );
}

/** Provides one object column and the same subtree selector in the grid and column manager. */
export default function useObjectColumn({
  breadcrumbField,
  organizationFilterKey,
  permissionSlug,
  legacyColumnIds = [],
}: ObjectColumnOptions) {
  const departmentText = useDepartmentText();
  const { mode } = useDepartmentUiMode();
  const { getAction } = useApi();
  const [hierarchyState, setHierarchyState] = useState<HierarchyState>({
    status: mode === 'legacy' ? 'ready' : 'loading',
    hierarchies: [],
    error: null,
  });

  useEffect(() => {
    if (mode === 'legacy') {
      setHierarchyState({ status: 'ready', hierarchies: [], error: null });
      return;
    }

    let active = true;
    setHierarchyState({ status: 'loading', hierarchies: [], error: null });
    getAction('HierarchyAvailableLevels')({ query: { filters: { 'permissionSlug.eq': permissionSlug } } })
      .then((availableHierarchies) => {
        if (active) {
          setHierarchyState({
            status: 'ready',
            hierarchies: orderAvailableHierarchies(Array.isArray(availableHierarchies) ? availableHierarchies : []),
            error: null,
          });
        }
      })
      .catch((error) => {
        panic(error);
        if (active) {
          setHierarchyState({
            status: 'error',
            hierarchies: [],
            error: error instanceof Error ? error : new Error('Exportné úrovne hierarchie sa nepodarilo načítať.'),
          });
        }
      });

    return () => {
      active = false;
    };
  }, [getAction, mode, permissionSlug]);

  const exportColumns = useMemo<ColDef[]>(() => {
    if (mode === 'legacy' || hierarchyState.status !== 'ready') return [];

    const labelHierarchies = new Map<string, Set<number>>();
    hierarchyState.hierarchies.forEach((hierarchy) => {
      hierarchy.levels.forEach(({ label }) => {
        const hierarchyIds = labelHierarchies.get(label) ?? new Set<number>();
        hierarchyIds.add(hierarchy.hierarchyId);
        labelHierarchies.set(label, hierarchyIds);
      });
    });
    const duplicateLabels = new Set(
      [...labelHierarchies].flatMap(([label, hierarchyIds]) => (hierarchyIds.size > 1 ? [label] : []))
    );

    return hierarchyState.hierarchies.flatMap((hierarchy) =>
      hierarchy.levels.map(({ hierarchyLevelId, slug, label }) => ({
        field: `${EXPORT_COLUMN_PREFIX}.${hierarchyLevelId}`,
        headerName: hierarchyLevelHeader(hierarchy, duplicateLabels, label),
        hide: true,
        getQuickFilterText: () => '',
        valueGetter: ({ data }: { data: Record<string, any> }) =>
          rowBreadcrumbs(data, breadcrumbField).find(({ hierarchyLevelSlug }) => hierarchyLevelSlug === slug)
            ?.departmentName ?? null,
      }))
    );
  }, [breadcrumbField, hierarchyState, mode]);

  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: OBJECT_COLUMN_ID,
        headerName: departmentText('Položka úrovne', 'Stredisko'),
        minWidth: 275,
        sortable: true,
        unSortIcon: true,
        filter: ObjectTreeColumnFilter,
        filterParams: { breadcrumbField },
        floatingFilter: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
        floatingFilterComponent: forwardRef(function BoundObjectTreeFloatingFilter(
          props: {
            api: GridApi;
            onChange?: (filters: Record<string, any>) => void;
            filterModel?: Record<string, any>;
          },
          ref
        ) {
          return (
            <ObjectTreeFloatingFilter
              {...props}
              ref={ref}
              organizationFilterKey={organizationFilterKey}
              permissionSlug={permissionSlug}
            />
          );
        }),
        valueGetter: ({ data }: { data: Record<string, any> }) => {
          const breadcrumbs = rowBreadcrumbs(data, breadcrumbField);
          return breadcrumbs[breadcrumbs.length - 1]?.departmentName ?? null;
        },
        cellRenderer: ObjectCell,
        cellRendererParams: { breadcrumbField },
      },
      ...exportColumns,
    ],
    [breadcrumbField, departmentText, exportColumns, organizationFilterKey, permissionSlug]
  );

  const profileColumnAliases = useMemo(
    () => Object.fromEntries(legacyColumnIds.map((columnId) => [columnId, OBJECT_COLUMN_ID])),
    [legacyColumnIds.join(',')]
  );

  const exportColumnKeys = useMemo(
    () =>
      mode === 'legacy' || hierarchyState.status !== 'ready'
        ? [OBJECT_COLUMN_ID]
        : exportColumns.map(({ field }) => field!),
    [exportColumns, hierarchyState.status, mode]
  );
  const columnReplacements = useMemo<Record<string, string[]>>(() => {
    if (mode === 'tree' && hierarchyState.status === 'ready') {
      return { [OBJECT_COLUMN_ID]: exportColumnKeys };
    }
    return {} as Record<string, string[]>;
  }, [exportColumnKeys, hierarchyState.status, mode]);

  return {
    columns,
    columnKeys: exportColumnKeys,
    columnReplacements,
    exportError: hierarchyState.error,
    exportReady: hierarchyState.status === 'ready',
    filterColumnKeys: [OBJECT_COLUMN_ID],
    profileMigrationReady: hierarchyState.status !== 'loading',
    profileColumnAliases,
  };
}
